import { v4 as uuid } from 'uuid'

import { ButtonType } from 'common/builder/constants/ButtonType'
import { ICloneSmartLink, ICreateButton } from 'components/MCTextEditor/MCEditorContextInterfaces'
import { findWhileTextChanges, IFindCallback } from 'components/MCTextEditor/shared/matcher'
import { SliceType } from 'components/MCTextEditor/slice/types'
import { INDEX_CHAR } from 'components/RichTextEditor/constants'
import {
  createDelete,
  createInsert,
} from 'components/RichTextEditor/processor/actions/textProcessorActions'
import {
  IProcessorState,
  ISlice,
  ITextProcessorMiddleware,
  PASTE_SOURCE,
} from 'components/RichTextEditor/processor/constants'
import { createFragment } from 'components/RichTextEditor/processor/range'
import { getText } from 'components/RichTextEditor/processor/selectors/textProcessorSelectors'
import { URL_REGEX } from 'constants/RegExs'

const URLPattern = `(^|\\s)(${URL_REGEX.source})`

const findURLs = (getState: () => IProcessorState, callback: IFindCallback) => {
  return findWhileTextChanges(
    () => getText(getState()),
    URLPattern,
    (index, match) => {
      const withoutSpace = match.replace(/^\s/, '')
      const lengthDiff = match.length - withoutSpace.length
      callback(index + lengthDiff, withoutSpace)
    },
  )
}

export const createInsertSmartLink =
  (createButton: ICreateButton, cloneSmartLink: ICloneSmartLink): ITextProcessorMiddleware =>
  (api) =>
  (next) =>
  (action) => {
    if (!action || action.type !== 'insert') {
      next(action)
      return
    }

    if (action.source === PASTE_SOURCE && action.slices) {
      const corruptedSlices: ISlice[] = []
      const smartLinks = (action.slices || []).filter((s) => s.type === SliceType.SMART_LINK)

      smartLinks.forEach((slice) => {
        const data = slice.data as { buttonId: string }
        const clonedButton = cloneSmartLink(data.buttonId)
        if (!clonedButton) {
          corruptedSlices.push(slice)
          return
        }

        data.buttonId = clonedButton.id
      })

      action.slices = action.slices.filter((s) => !corruptedSlices.includes(s))
    }

    next(action)

    findURLs(api.getState, (index, dataString) => {
      const existingSlice = action.slices?.find(
        (s) => s.type === SliceType.SMART_LINK && s.range.start === index,
      )
      if (existingSlice) {
        return
      }

      api.dispatch(createDelete(index, dataString.length))
      const buttonId = createButton(ButtonType.URL, dataString)
      if (buttonId) {
        const label = `${INDEX_CHAR}${dataString}${INDEX_CHAR}`

        const smartLinkSlice = createFragment(0, label.length, {
          type: SliceType.SMART_LINK,
          data: { buttonId },
          id: uuid(),
        })

        api.dispatch(createInsert(index, label, [smartLinkSlice]))
      }
    })
  }
